import styled from "styled-components";
import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useWindowDimensions, detectSpaceWhite, theme } from "@utilities";
import moment from "moment";
import {
    BreadcrumbsCommon,
    ProductDetailComponent,
    DetailTourMobileComponent,
    TourNeedToKnowMobile,
    StyledTourDetail,
    StyledNeedToKnow,
    NeedToKnowTagCommon,
    NeedToKnowContentCommon,
    ClipLoaderCommon,
    PageHead,
} from "@components";
import {
    getSiteMeta,
    readStartDateParam,
    buildProductJsonLd,
    buildTouristTripJsonLd,
    buildBreadcrumbJsonLd,
    stripHtml,
} from "@utilities";
import { AppModuleLayout } from "@layouts";
import { PATH_DETAIL_TOUR, PATH_HOME, PATH_SEARCH_RESULT } from "@routes";
import {
    selectHomePageProduct,
    setSupplier,
    setName,
    useAppDispatch,
    useAppSelector,
} from "@redux";
import { IProduct, IProductDetail } from "@interfaces";
import { ProductPrices } from "src/Module/Checkout/Components/ProductPricings";
import {
    formatProductResponse,
    getProductHelper,
    getProductFromCache,
    getProductImages,
} from "src/Module/Checkout/productHelper";
import _ from "lodash";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { getViewItemCategories } from "@utilities";

export const DetailTour = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const { homePageProducts } = useAppSelector(selectHomePageProduct);

    // Deterministic fallback title on mount so the tab/analytics never show the
    // previous route's title while the tour is still loading. renderTourHead's
    // PageHead replaces it with the real tour title once data resolves.
    useEffect(() => {
        document.title = theme.title;
    }, [id]);

    // Accepts startdate / startDate / start_date — external sites have linked with
    // all three, and a lowercase-only read silently ignored the latter two.
    const startDateParam = readStartDateParam(searchParams);
    const dateRange = startDateParam
        ? {
              startDate: moment(startDateParam, "YYYY-MM-DD").toDate(),
              endDate: moment(startDateParam, "YYYY-MM-DD").add(7, "days").toDate(),
              selectedDate: moment(startDateParam, "YYYY-MM-DD").toDate(),
          }
        : null;
    //page state
    const [currentNeedToKnowIndex, setCurrentNeedToKnowIndex] = useState<number>(0);
    const [needToKnowOpen, setNeedToKnowOpen] = useState(false);
    //page variable
    const { width } = useWindowDimensions();
    const fareTypeRef = useRef<HTMLDivElement>(null);
    const pricingSectionRef = useRef<HTMLDivElement>(null);
    const hasScrolledToPricesRef = useRef<boolean>(false);
    const hasScrolledToPricesMobileRef = useRef<boolean>(false);
    const lastViewItemProductIdRef = useRef<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [tour, setTour] = useState<IProductDetail | null>();
    const [apiTour, setApiTour] = useState<IProductDetail | null>();
    const [isPriceLoading, setIsPriceLoading] = useState<boolean>(false);

    // Check if user came from search result page
    const cameFromSearchResult = (location.state as any)?.fromSearchResult === true;

    const breadcrumbsData = [
        {
            title: "Home",
            link: PATH_HOME,
        },
        ...(cameFromSearchResult
            ? [
                  {
                      title: "Search result",
                      link: PATH_SEARCH_RESULT,
                  },
              ]
            : []),
        {
            title: "Details",
            link: PATH_DETAIL_TOUR + "/" + id,
        },
    ];
    const handleSwitchNeedToKnow = (index: number) => {
        setCurrentNeedToKnowIndex(index);
    };

    const handleClickSupplier = () => {
        const supplierText = tour?.supplier || tour?.supplierName;
        if (supplierText) {
            dispatch(setName(""));
            dispatch(setSupplier([{ id: supplierText, text: supplierText }]));
            navigate(PATH_SEARCH_RESULT);
        }
    };

    //webview application
    const webView = tour && (
        <Fragment>
            <ProductDetailComponent
                breadcrumbsData={breadcrumbsData}
                data={tour}
                handleClickSupplier={handleClickSupplier}
                isPriceLoading={isPriceLoading}
            />
            <div
                className="fareType"
                ref={fareTypeRef}
                style={{ scrollMarginTop: width >= 1200 ? 168 : width >= 1024 ? 210 : 150 }}
            >
                <ProductPrices
                    product={tour}
                    initialDateRange={dateRange}
                    isProductFullyLoaded={!isPriceLoading}
                />
            </div>
            <div className="fareType">
                <StyledTourDetail>
                    <div
                        className="heading"
                        onClick={() => setNeedToKnowOpen((prev) => !prev)}
                        style={{ cursor: "pointer", userSelect: "none" }}
                    >
                        <h3>Need to know</h3>
                        <CollapseHint>
                            <CollapseHintText>
                                {needToKnowOpen ? "click to collapse" : "click to expand"}
                            </CollapseHintText>
                            <CollapseArrow $isOpen={needToKnowOpen}>
                                <svg viewBox="0 0 24 24" width="22" height="22">
                                    <path
                                        d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z"
                                        fill="currentColor"
                                    />
                                </svg>
                            </CollapseArrow>
                        </CollapseHint>
                    </div>
                    <CollapsibleBody $isOpen={needToKnowOpen}>
                        <StyledNeedToKnow>
                            {tour.description && (
                                <>
                                    <div className="listTags">
                                        {tour.description.map((item, index) => {
                                            if (detectSpaceWhite(item.content)) return;
                                            return (
                                                <NeedToKnowTagCommon
                                                    isActive={currentNeedToKnowIndex === index}
                                                    key={index}
                                                    onSwitchNeedToKnow={() => {
                                                        handleSwitchNeedToKnow(index);
                                                    }}
                                                    title={item.title}
                                                />
                                            );
                                        })}
                                    </div>
                                    <div className="listContentWrapper">
                                        <div className="listContents">
                                            {tour.description.map((item, index) => {
                                                const cleanedContent = item.content?.replace(
                                                    /<p>(\s|&nbsp;)*<\/p>/gi,
                                                    "",
                                                );
                                                return (
                                                    <NeedToKnowContentCommon
                                                        key={index}
                                                        isActive={currentNeedToKnowIndex === index}
                                                        content={cleanedContent || ""}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            )}
                        </StyledNeedToKnow>
                    </CollapsibleBody>
                </StyledTourDetail>
            </div>
        </Fragment>
    );

    const mobileView = tour && (
        <MobileViewContainer>
            {/* The mobile tour view is a separate tree from the desktop one, which
                carries its breadcrumbs inside ProductDetailComponent — so they have
                to be rendered here rather than revealed with a media query. */}
            <MobileBreadcrumbs>
                <BreadcrumbsCommon data={breadcrumbsData} />
            </MobileBreadcrumbs>
            <DetailTourMobileComponent tour={tour} handleClickSupplier={handleClickSupplier} />
            <div className="pricingSection" ref={pricingSectionRef}>
                <ProductPrices
                    product={tour}
                    initialDateRange={dateRange}
                    isProductFullyLoaded={!isPriceLoading}
                />
            </div>
            <TourNeedToKnowMobile data={tour.description} />
        </MobileViewContainer>
    );

    const handleBack = () => {
        window.history.back();
    };

    useEffect(() => {
        hasScrolledToPricesRef.current = false;
        hasScrolledToPricesMobileRef.current = false;

        const fetchTour = async () => {
            try {
                if (id) {
                    let hasInitialData = false;

                    if (homePageProducts) {
                        const allTours = _.values(homePageProducts).flatMap(
                            (category) => category.tours,
                        );
                        const foundTour = allTours.find((t) => t.productId === id);
                        if (foundTour) {
                            try {
                                const formatted = formatProductResponse(
                                    foundTour as unknown as IProduct,
                                    id,
                                );
                                setTour(formatted.data);
                                setIsLoading(false);
                                setIsPriceLoading(true);
                                hasInitialData = true;
                            } catch {}
                        }
                    }

                    if (!hasInitialData) {
                        const cacheRes = await getProductFromCache(id.toString());
                        if (cacheRes.isSuccess && cacheRes.data) {
                            setTour(cacheRes.data);
                            setIsLoading(false);
                            setIsPriceLoading(true);
                            hasInitialData = true;
                        }
                    }

                    const [fullRes, images] = await Promise.all([
                        getProductHelper(id.toString(), hasInitialData),
                        hasInitialData ? getProductImages(id.toString()) : Promise.resolve(null),
                    ]);

                    if (fullRes.isSuccess && fullRes.data) {
                        const finalData = images ? { ...fullRes.data, images } : fullRes.data;
                        setApiTour(finalData);
                    } else if (fullRes.isSuccess && !fullRes.data) {
                        if (!hasInitialData) {
                            setTour(null);
                        }
                        setIsPriceLoading(false);
                    } else {
                        ErrorToast({ Message: fullRes.errorMessage ?? "Something went wrong" });
                        setIsPriceLoading(false);
                    }

                    if (!hasInitialData) {
                        setIsLoading(false);
                    }
                }
            } catch (error) {
                console.error("Error fetching product:", error);
                setIsLoading(false);
                setIsPriceLoading(false);
            }
        };
        fetchTour();
    }, [id, homePageProducts]);

    useEffect(() => {
        if (apiTour) {
            setTour(apiTour);
            setIsPriceLoading(false);
        }
    }, [apiTour]);

    // GA4 view_item: push when user arrives on product page (agency dataLayer spec)
    useEffect(() => {
        if (!tour?.productId) return;
        if (lastViewItemProductIdRef.current === tour.productId) return;
        lastViewItemProductIdRef.current = tour.productId;

        if (typeof window === "undefined") return;
        window.dataLayer = window.dataLayer || [];
        const price = Number(tour.rrp ?? 0) || 0;
        const { item_category, item_category2 } = getViewItemCategories(tour.categories as any);

        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
            event: "view_item",
            ecommerce: {
                currency: "AUD",
                value: price,
                items: [
                    {
                        item_id: tour.productId,
                        item_variant: String(tour.tourCode ?? ""),
                        item_name: tour.name,
                        item_category,
                        item_category2,
                        item_location_id: tour.location,
                        duration: tour.duration,
                        price,
                        quantity: 1,
                    },
                ],
            },
        });
    }, [tour]);

    // Scroll to the Product Prices section on every tour load, not only for
    // ?startdate= deep links: the calendar now opens on the next available day,
    // so the availability panel is worth landing on either way. (desktop only)
    useEffect(() => {
        if (
            tour &&
            !isLoading &&
            width >= 767 &&
            fareTypeRef.current &&
            !hasScrolledToPricesRef.current
        ) {
            // Use setTimeout to ensure DOM is fully rendered
            const timeoutId = setTimeout(() => {
                if (fareTypeRef.current) {
                    // scrollIntoView rather than offsetTop + window.scrollTo: the
                    // offset was measured against the nearest positioned ancestor
                    // rather than the page, and a smooth window scroll was being
                    // cancelled by the renders that follow availability loading.
                    // Header clearance comes from scrollMarginTop on the section.
                    fareTypeRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
                    hasScrolledToPricesRef.current = true;
                }
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [tour, isLoading, width]);

    // Same on mobile: land on the pricing section whether or not a date was named.
    useEffect(() => {
        if (
            tour &&
            !isLoading &&
            width < 767 &&
            pricingSectionRef.current &&
            !hasScrolledToPricesMobileRef.current
        ) {
            const timeoutId = setTimeout(() => {
                if (pricingSectionRef.current) {
                    pricingSectionRef.current.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    });
                    hasScrolledToPricesMobileRef.current = true;
                }
            }, 300); // Increased delay to ensure DOM is fully rendered

            return () => clearTimeout(timeoutId);
        }
    }, [tour, isLoading, width]);

    const renderTourHead = () => {
        if (!tour) return <Fragment />;
        const site = getSiteMeta();
        const path = `${PATH_DETAIL_TOUR}/${tour.productId}`;
        const url = site.siteUrl ? `${site.siteUrl}${path}` : path;
        const rawDesc =
            tour.productTeaser ||
            (Array.isArray(tour.description)
                ? tour.description.map((d) => d.content).join(" ")
                : tour.description) ||
            "";
        const description = stripHtml(String(rawDesc)) || `Book ${tour.name} with ${theme.title}.`;
        const images = [
            ...(Array.isArray(tour.images) ? tour.images.map((img) => img.path) : []),
            tour.productLargeSizeImagePath,
            tour.productImagePath,
        ].filter((v): v is string => !!v);
        const days = Number(tour.durationDays);
        const isoDuration = !isNaN(days) && days > 0 ? `P${days}D` : undefined;
        const rating = (tour as unknown as { rating?: number | null }).rating;

        const productInput = {
            name: tour.name,
            description,
            images,
            url,
            sku: tour.tourCode,
            brandName: tour.supplier || tour.supplierName || site.name,
            price: tour.rrp,
            priceCurrency: "AUD",
            isoDuration,
            ratingValue: rating ?? undefined,
        };

        const seo = {
            title: `${tour.name} | ${theme.title}`,
            metaDescription: description.slice(0, 300),
            canonicalUrl: url,
            ogImage: images[0],
            structuredDataType: "Product" as const,
        };

        const jsonLd = [
            buildProductJsonLd(productInput, site),
            buildTouristTripJsonLd(productInput, site),
            buildBreadcrumbJsonLd(
                breadcrumbsData.map((b) => ({ name: b.title, url: b.link })),
                site,
            ),
        ];

        return <PageHead seo={seo} jsonLd={jsonLd} />;
    };

    return (
        <AppModuleLayout onBack={handleBack}>
            {renderTourHead()}
            <Wrapper>
                {isLoading ? (
                    <ClipLoaderCommon color={theme.colors.highlight} size={70} margin={150} />
                ) : tour ? (
                    width >= 767 ? (
                        webView
                    ) : (
                        mobileView
                    )
                ) : (
                    <div className="noProduct">
                        <img src="/images/notFound.svg" alt="" />
                        <p className="line1">No Products Found</p>
                        <p className="line2">
                            Your search did not match any products. <br />
                            Please try again.
                        </p>
                    </div>
                )}
            </Wrapper>
        </AppModuleLayout>
    );
};

const CollapseHint = styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const CollapseHintText = styled.span`
    font-size: 13px;
    font-weight: 400;
    color: #fff;
    opacity: 0.8;
`;

const CollapseArrow = styled.span<{ $isOpen: boolean }>`
    display: flex;
    align-items: center;
    transition: transform 0.25s ease;
    transform: rotate(${(p) => (p.$isOpen ? "180deg" : "0deg")});
    color: #fff;
`;

const CollapsibleBody = styled.div<{ $isOpen: boolean }>`
    max-height: ${(p) => (p.$isOpen ? "2000px" : "0")};
    overflow: hidden;
    transition: max-height 0.35s ease;
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    flex: 1;

    .fareType {
        background-color: #f2f2f2;

        &:last-child {
            padding-bottom: 100px;
        }
    }

    .noProduct {
        text-align: center;
        padding-top: 20px;
        font-size: 22px;

        .line1 {
            font-weight: 700;
            padding-bottom: 20px;
        }

        .line2 {
            font-size: 18px;
            line-height: 22px;
        }
    }
`;

const MobileBreadcrumbs = styled.div`
    padding: 12px 16px;
    /* The trail can outrun a phone's width, so it scrolls sideways rather than
       wrapping into a second line above the hero image. */
    overflow-x: auto;
    white-space: nowrap;

    &::-webkit-scrollbar {
        display: none;
    }
    scrollbar-width: none;
`;

const MobileViewContainer = styled.div`
    width: 100%;

    padding-bottom: 100px;

    .pricingSection {
        background-color: #f2f2f2;
    }
`;
